/**
 * The mail, first: a compact "Read the mail" card over the draft table with the sender,
 * the customer line (confirmed, to confirm, or to find), the attachments as chips, the
 * customer's own text cut to six lines with "Read all", the earlier messages folded, and
 * the thread position in one line above. Read from the case store (never from the
 * mailbox). A mail in another language than the viewer's is shown translated, with the
 * original one click away (`mail-translation.tsx`). A row of the draft marks its words
 * here (`focusText`).
 */

import {
  Camera,
  FileSpreadsheet,
  Icon,
  type IconComponent,
  Languages,
  Mail,
  Paperclip,
  RefreshCw,
} from "@norican/icons";
import { Button, Card, cn, Collapsible, Skeleton, Typography } from "@norican/react";
import { useEffect, useRef, useState } from "react";

import { ColleagueNotes, ThreadLine } from "@/features/case-review/case-links";
import { CustomerLine, type CustomerLineProps } from "@/features/case-review/customer-line";
import { CustomerParties } from "@/features/case-review/customer-parties";
import { focusMailText } from "@/features/case-review/mail-focus";
import {
  TranslationNotice,
  useMailTranslation,
  ViewerLanguageSelect,
} from "@/features/case-review/mail-translation";
import {
  type CaseMail,
  type CaseMailAttachment,
  useCaseMail,
  useRequestMailRestore,
} from "@/lib/api";
import { isOutOfScopeCase } from "@/lib/intents";
import { logger } from "@/lib/logger";
import {
  cleanMailText,
  compactMailLines,
  hasGatewayBanner,
  missingMailText,
  removedMailText,
} from "@/lib/mail-text";
import { type QuoteCaseDetail } from "@/lib/mock";
import { STEP_NAMES } from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { formatDayTime } from "@/lib/work-labels";

/** Lines of the mail text shown before "Read all", on every width. */
export const CLAMP_LINES = 6;

/** The icon of an attachment chip by what the file is: a sheet, a picture, anything else. */
export function attachmentIcon(
  attachment: Pick<CaseMailAttachment, "name" | "content_type">,
): IconComponent {
  const name = attachment.name.toLowerCase();
  if (
    attachment.content_type.includes("spreadsheet") ||
    attachment.content_type.includes("excel") ||
    /\.(xlsx|xlsm|xls|csv)$/.test(name)
  ) {
    return FileSpreadsheet;
  }
  if (attachment.content_type.startsWith("image/")) {
    return Camera;
  }
  return Paperclip;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_048_576) {
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
}

function formatAddress(name: string | null, email: string | null): string {
  if (name && email && name !== email) {
    return `${name} <${email}>`;
  }
  return name || email || "unknown sender";
}

/** One attachment as a chip-shaped download link (the API answers `Content-Disposition: attachment`). */
function AttachmentLink({
  caseId,
  attachment,
}: {
  caseId: string;
  attachment: CaseMailAttachment;
}) {
  return (
    <a
      href={`/api/cases/${encodeURIComponent(caseId)}/attachments/${attachment.index}`}
      download={attachment.name}
      className="bg-info-subtle text-info-fg inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold hover:underline"
      title={`Download ${attachment.name}`}
      onClick={() => {
        logger.debug("[case] download attachment", { caseId, index: attachment.index });
        track("case.attachment_open", { kind: "file" }, { caseId });
      }}
    >
      <Icon icon={attachmentIcon(attachment)} size="xs" />
      <span className="truncate">{attachment.name}</span>
      <span className="text-fg-subtle shrink-0 font-normal">
        {formatBytes(attachment.size_bytes)}
      </span>
    </a>
  );
}

/** An attachment of a removed original: its name and size, not openable. */
function AttachmentName({ attachment }: { attachment: CaseMailAttachment }) {
  return (
    <span
      className="bg-bg-subtle text-fg-muted inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold"
      title={`${attachment.name} (fetch the original again to open it)`}
    >
      <Icon icon={attachmentIcon(attachment)} size="xs" />
      <span className="truncate">{attachment.name}</span>
      <span className="text-fg-subtle shrink-0 font-normal">
        {formatBytes(attachment.size_bytes)}
      </span>
    </span>
  );
}

/** Notice above a removed original, with "Fetch again" while that can still help. */
function RemovedMailNotice({ caseId, mail }: { caseId: string; mail: CaseMail }) {
  const restore = useRequestMailRestore(caseId);
  const canFetch = mail.restore === "none" || mail.restore == null;
  return (
    <div className="border-border-subtle flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
      <Typography variant="body-sm" tone="muted" className="min-w-0 flex-1">
        {removedMailText(mail)}
      </Typography>
      {canFetch && (
        <Button
          variant="outline"
          size="sm"
          startIcon={<Icon icon={RefreshCw} size="xs" />}
          loading={restore.isPending}
          onClick={() => restore.mutate()}
        >
          Fetch again
        </Button>
      )}
      {restore.isError && (
        <Typography variant="body-sm" tone="danger">
          The request did not go through. Try again in a moment.
        </Typography>
      )}
    </div>
  );
}

/** A mail I2Q could not read and never stored, with "Fetch again" (sim360 P17). */
function MissingMailNotice({ caseId, mail }: { caseId: string; mail: CaseMail }) {
  const restore = useRequestMailRestore(caseId);
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3">
      <Typography variant="body-sm" tone="muted" className="min-w-0 flex-1">
        {missingMailText(mail)}
      </Typography>
      {mail.can_fetch_again && (
        <Button
          variant="outline"
          size="sm"
          startIcon={<Icon icon={RefreshCw} size="xs" />}
          loading={restore.isPending}
          onClick={() => restore.mutate()}
        >
          Fetch again
        </Button>
      )}
      {restore.isError && (
        <Typography variant="body-sm" tone="danger">
          The request did not go through. Try again in a moment.
        </Typography>
      )}
    </div>
  );
}

/**
 * The mail body: the new text, then the quoted history behind "Show earlier mails".
 * `translated` replaces the new text with its translation; the earlier mails stay as
 * written. `clamp` shows the first six lines with "Read all" (the card is compact on
 * every width since round 2, so the draft table stays in view).
 */
export function MailBody({
  mail,
  translated = null,
  clamp = true,
}: {
  mail: CaseMail;
  translated?: string | null;
  clamp?: boolean;
}) {
  const [showEarlier, setShowEarlier] = useState(false);
  const [readAll, setReadAll] = useState(false);
  const body = mail.body_text ?? "";
  const cut = mail.quoted_from != null && mail.quoted_from > 0 ? mail.quoted_from : null;
  // Cut first (the index is into the raw body), then clean each part for display.
  const current = cleanMailText(translated ?? (cut == null ? body : body.slice(0, cut)));
  const earlier = cleanMailText(cut == null ? "" : body.slice(cut));
  const textClass =
    "text-fg text-[0.8125rem] leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap font-sans";
  // Folded: the customer's own lines without blanks and without the gateway banner, cut
  // to six; "Read all" only when the fold hides words (more lines, the banner, or a line
  // long enough to wrap past the clamp).
  const compact = compactMailLines(current);
  const folded = clamp && !readAll;
  const clamped =
    folded &&
    (compact.length > CLAMP_LINES ||
      hasGatewayBanner(current) ||
      compact.some((line) => line.length > 110));
  const shown = folded ? compact.slice(0, CLAMP_LINES).join("\n") : current.trimEnd();

  if (body.trim() === "" && !translated?.trim()) {
    return (
      <Typography variant="body-sm" tone="muted">
        This mail has no text. Check the attachments.
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(textClass, clamped && "line-clamp-6")}
        data-mail-clamped={clamped || undefined}
      >
        {shown}
      </div>
      {clamped && (
        <Button
          size="sm"
          variant="ghost"
          tone="accent"
          className="w-fit"
          onClick={() => {
            track("case.mail_unfold", { part: "read_all" });
            setReadAll(true);
          }}
        >
          Read all
        </Button>
      )}
      {earlier.trim() !== "" && (
        <Collapsible.Root
          open={showEarlier}
          onOpenChange={(next) => {
            if (next) {
              track("case.mail_unfold", { part: "earlier" });
            }
            setShowEarlier(next);
          }}
        >
          <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
            {showEarlier ? "Hide earlier mails" : "Show earlier mails"}
          </Collapsible.Trigger>
          <Collapsible.Panel>
            {translated != null && (
              <Typography variant="caption" tone="subtle" className="mt-2 block">
                The earlier mails stay as written.
              </Typography>
            )}
            <div className={cn(textClass, "border-border text-fg-muted mt-2 border-l-2 pl-3")}>
              {earlier.trim()}
            </div>
          </Collapsible.Panel>
        </Collapsible.Root>
      )}
      {mail.body_truncated && (
        <Typography variant="caption" tone="subtle">
          This mail is very long. You see the first part only.
        </Typography>
      )}
    </div>
  );
}

/**
 * The mail card: "Read the mail" with the language, the sender, the customer line (on the
 * page), the attachments, the text cut to six lines. A mail in another language than
 * the viewer's is shown translated, with the original one click away. The translated
 * subject is shown when it differs from the page title.
 */
export function SourceCard({
  caseId,
  className,
  autoTranslate = true,
  focusText = null,
  focusReveal = 0,
  customer = null,
}: {
  caseId: string;
  className?: string;
  /** False for a mail that is not an inquiry: translated only when the viewer asks. */
  autoTranslate?: boolean;
  /** The chosen line's text to mark in the mail (P28); null for none. */
  focusText?: readonly string[] | null;
  /**
   * Counts the clicks on a row's customer words: each new count brings the marked text
   * into view (the card unfolds when the text sits behind "Read all"); an unchanged
   * count (a chevron opened, the bar focused a row) marks the text and leaves the page
   * where the person works.
   */
  focusReveal?: number;
  /** The customer line under the sender (the page); null leaves it out (the training stages). */
  customer?: CustomerLineProps | null;
}) {
  const { data: mail, isLoading, isError } = useCaseMail(caseId);
  const translation = useMailTranslation(caseId, mail, autoTranslate);
  const view = translation.view;
  const mailRef = useRef<HTMLDivElement>(null);
  const focusKey = (focusText ?? []).join("\n");
  const shownText = view?.current ?? mail?.body_text ?? "";
  // "Read all" pressed by the page itself, because a reveal pointed behind the fold.
  const [unfolded, setUnfolded] = useState(false);
  // The last reveal count that was brought into view (or looked for and not found).
  const revealedRef = useRef(0);
  useEffect(() => {
    // After the text is on screen: mark the chosen line, and scroll to it on a reveal.
    if (!focusKey) {
      return;
    }
    const reveal = focusReveal > revealedRef.current;
    const result = focusMailText(mailRef.current, focusKey.split("\n"), { scroll: reveal });
    if (reveal && result === "clipped") {
      // Unfold the mail; this effect runs again with the text visible and scrolls then.
      logger.debug("[case] unfold mail for the chosen line", { caseId });
      setUnfolded(true);
      return;
    }
    revealedRef.current = focusReveal;
  }, [caseId, focusKey, focusReveal, shownText, unfolded]);
  // Only an embedded picture is hidden behind the count; any other part is a file to open.
  const files = (mail?.attachments ?? []).filter(
    (a) => !a.inline || !a.content_type.startsWith("image/"),
  );
  const inlineCount = (mail?.attachments ?? []).length - files.length;

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className="flex flex-col gap-2 p-4" aria-busy="true">
        <Skeleton variant="rect" height={20} />
        <Skeleton variant="rect" height={160} />
      </div>
    );
  } else if (isError || !mail) {
    content = (
      <p className="text-fg-muted px-4 py-3 text-sm">
        I2Q could not load the original mail. Reload the page to try again.
      </p>
    );
  } else if (!mail.available) {
    content = missingMailText(mail) ? (
      <MissingMailNotice caseId={caseId} mail={mail} />
    ) : (
      <p className="text-fg-muted px-4 py-3 text-sm">
        The mail text appears once I2Q has read this mail.
      </p>
    );
  } else {
    const received = formatDayTime(mail.received_at);
    const removed = mail.original === "removed";
    // A mail without a subject gets none from the translation either: the translator
    // then returns the first line, which on a gateway mail is the external-sender
    // banner (seen on a Czech mail, tester round 5, 12 Oct 2026).
    const translatedSubject =
      view?.subject &&
      view.subject !== mail.subject &&
      (mail.subject ?? "").trim() !== "" &&
      !hasGatewayBanner(view.subject)
        ? view.subject
        : null;
    content = (
      <div className="flex flex-col">
        {removed && <RemovedMailNotice caseId={caseId} mail={mail} />}
        <div className="border-border-subtle flex flex-col gap-1.5 border-b px-4 py-3">
          {translatedSubject && (
            <Typography as="h2" variant="label" className="text-fg font-bold break-words">
              {translatedSubject}
            </Typography>
          )}
          <Typography variant="body-sm" className="text-fg break-words">
            <span className="text-fg-subtle">From </span>
            {formatAddress(mail.from_name, mail.from_email)}
            {received && <span className="text-fg-subtle"> · {received}</span>}
          </Typography>
          {customer && <CustomerLine {...customer} />}
          {customer && <CustomerParties detail={customer.detail} />}
          {translation.canRequest ? (
            <div>
              <Button
                variant="ghost"
                tone="accent"
                size="sm"
                className="h-auto px-1 py-0 text-xs"
                startIcon={<Icon icon={Languages} size="xs" />}
                onClick={translation.request}
              >
                Translate
              </Button>
            </div>
          ) : (
            view && <TranslationNotice view={view} onToggle={translation.toggleOriginal} />
          )}
          {(files.length > 0 || inlineCount > 0) && (
            <div className="flex flex-wrap items-center gap-2" data-attachment-chips>
              {files.map((attachment) =>
                removed ? (
                  <AttachmentName key={attachment.index} attachment={attachment} />
                ) : (
                  <AttachmentLink key={attachment.index} caseId={caseId} attachment={attachment} />
                ),
              )}
              {inlineCount > 0 && (
                <span className="text-fg-subtle text-xs">
                  +{inlineCount} inline {inlineCount === 1 ? "image" : "images"}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3" ref={mailRef}>
          {mail.body_source === "too_large" ? (
            <Typography variant="body-sm" tone="muted">
              This mail is too large to display here.
            </Typography>
          ) : (
            <MailBody mail={mail} translated={view?.current ?? null} clamp={!unfolded} />
          )}
        </div>
      </div>
    );
  }

  return (
    <Card variant="outline" className={cn("flex min-w-0 flex-col overflow-hidden", className)}>
      <Card.Header
        padding="sm"
        className="border-border-subtle flex items-center justify-between border-b"
      >
        <Typography
          as="span"
          variant="micro"
          className="text-fg-subtle inline-flex shrink-0 items-center gap-1.5 font-bold whitespace-nowrap"
        >
          <Icon icon={Mail} size="xs" />
          {STEP_NAMES.read}
        </Typography>
        {mail?.available && (
          <ViewerLanguageSelect
            value={translation.language}
            onChange={translation.chooseLanguage}
          />
        )}
      </Card.Header>
      {content}
    </Card>
  );
}

/** The mail block of the page: the thread position in one line, then the compact card. */
export function MailColumn({
  detail,
  focusText = null,
  focusReveal = 0,
  customer = null,
  className,
}: {
  detail: QuoteCaseDetail;
  focusText?: readonly string[] | null;
  /** See `SourceCard`: a new count scrolls the marked text into view. */
  focusReveal?: number;
  /** The customer line inside the card; null for a mail that is not an inquiry. */
  customer?: Omit<CustomerLineProps, "detail"> | null;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)} data-mail-column>
      <ThreadLine detail={detail} />
      <ColleagueNotes detail={detail} />
      {/* A mail that is not an inquiry is translated only when the viewer asks. */}
      <SourceCard
        caseId={detail.id}
        autoTranslate={!isOutOfScopeCase(detail)}
        focusText={focusText}
        focusReveal={focusReveal}
        customer={customer ? { detail, ...customer } : null}
      />
    </div>
  );
}
